import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  Code, 
  Sparkles, 
  Home,
  Wrench,
  CheckCircle,
  Zap,
  TrendingUp,
  Shield,
  Clock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// ============================================
// BANNER 1: DEVS MEI
// ============================================
const BannerDevs = ({ onNavigate }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative min-h-[600px] md:min-h-[700px] rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
      }}
    >
      {/* Código de fundo animado */}
      <div className="absolute inset-0 opacity-10 overflow-hidden">
        <motion.pre
          animate={{ y: [0, -500] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          className="text-xs text-indigo-300 whitespace-pre font-mono leading-relaxed p-4"
        >
{`const mei = new MeuMei();
await mei.emitirNota({ valor: 5000 });
await mei.gerarDAS();

function calcularImpostos(faturamento) {
  return faturamento * 0.05;
}

const freelancer = {
  cnpj: '00.000.000/0001-00',
  atividade: 'Desenvolvimento',
  faturamento: 81000
};

// Automatize sua vida MEI
mei.subscribe({ plan: 'pro' });

export default MeuMei;`}
        </motion.pre>
      </div>

      {/* Efeitos de luz */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-violet-500/20 rounded-full blur-[100px]" />

      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.5)',
            }}
          >
            <Code size={18} className="text-indigo-400" />
            <span className="text-sm font-bold text-indigo-300 uppercase tracking-wide">
              Para Desenvolvedores
            </span>
          </motion.div>

          {/* Título */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-6xl font-black text-white leading-tight mb-6"
          >
            DEV, PARE DE{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400">
              PERDER DINHEIRO
            </span>{' '}
            COM BUROCRACIA
          </motion.h2>

          {/* Subtítulo */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-300 mb-8"
          >
            Você programa sistemas complexos, mas ainda perde horas emitindo nota e calculando DAS? 
            <span className="text-indigo-400 font-semibold"> Automatize isso.</span>
          </motion.p>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            {[
              'Emita NF em 30 segundos',
              'DAS calculado automaticamente',
              'Controle de clientes',
              'Relatórios de faturamento'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-300">
                <CheckCircle size={18} className="text-indigo-400 flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(99, 102, 241, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate('https://app.meumei.net/register')}
            className="group px-8 py-4 rounded-xl font-bold text-white flex items-center gap-3"
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 15px 40px rgba(99, 102, 241, 0.3)',
            }}
          >
            <Code size={20} />
            COMEÇAR AGORA - 7 DIAS GRÁTIS
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>

          {/* Stat */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 text-sm text-gray-500"
          >
            <span className="text-indigo-400 font-bold">+500 devs</span> já automatizaram seu MEI
          </motion.p>
        </div>
      </div>

      {/* Terminal decorativo */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="hidden lg:block absolute bottom-10 right-10 w-80 rounded-xl overflow-hidden"
        style={{
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div className="flex items-center gap-2 px-4 py-2 bg-black/50">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="ml-2 text-xs text-gray-500">terminal</span>
        </div>
        <div className="p-4 font-mono text-sm">
          <p className="text-gray-500">$ meumei status</p>
          <p className="text-green-400">✓ DAS em dia</p>
          <p className="text-green-400">✓ NFs emitidas: 12</p>
          <p className="text-green-400">✓ Faturamento: R$ 6.500</p>
          <p className="text-indigo-400 mt-2">$ meumei --relax 🚀</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// BANNER 2: EMPREGADAS DOMÉSTICAS MEI
// ============================================
const BannerDomesticas = ({ onNavigate }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative min-h-[600px] md:min-h-[700px] rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 50%, #f5d0fe 100%)',
      }}
    >
      {/* Elementos decorativos */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-pink-300/30 rounded-full blur-[60px]" />
      <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-300/30 rounded-full blur-[60px]" />
      
      {/* Ícones flutuantes */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute top-20 right-20 text-pink-300/50"
      >
        <Home size={80} />
      </motion.div>
      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute bottom-32 right-32 text-purple-300/50"
      >
        <Sparkles size={60} />
      </motion.div>

      <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
              border: '1px solid rgba(236, 72, 153, 0.4)',
            }}
          >
            <Home size={18} className="text-pink-600" />
            <span className="text-sm font-bold text-pink-700 uppercase tracking-wide">
              Para Profissionais Domésticas
            </span>
          </motion.div>

          {/* Título */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-6xl font-black text-gray-800 leading-tight mb-6"
          >
            SEU TRABALHO VALE{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">
              MUITO MAIS
            </span>{' '}
            COM CNPJ
          </motion.h2>

          {/* Subtítulo */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600 mb-8"
          >
            Diarista, faxineira, cuidadora? Formalize seu trabalho, cobre mais e tenha acesso a{' '}
            <span className="text-pink-600 font-semibold">benefícios do INSS!</span>
          </motion.p>

          {/* Benefits cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            {[
              { icon: Shield, text: 'Aposentadoria garantida' },
              { icon: TrendingUp, text: 'Aumente seu valor/hora' },
              { icon: Zap, text: 'Nota fiscal fácil' },
              { icon: Clock, text: 'Pague só R$ 70/mês' }
            ].map((item, i) => (
              <div 
                key={i} 
                className="flex items-center gap-3 p-3 rounded-xl bg-white/60 backdrop-blur-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                  <item.icon size={20} className="text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">{item.text}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(236, 72, 153, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate('https://app.meumei.net/register')}
            className="group px-8 py-4 rounded-xl font-bold text-white flex items-center gap-3"
            style={{
              background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
              boxShadow: '0 15px 40px rgba(236, 72, 153, 0.3)',
            }}
          >
            <Sparkles size={20} />
            QUERO MEU CNPJ AGORA
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>

          {/* Depoimento */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-8 p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-pink-200"
          >
            <p className="text-gray-600 italic text-sm">
              "Depois que abri meu MEI, meus clientes me valorizam mais. Hoje cobro 40% a mais!"
            </p>
            <p className="text-pink-600 font-semibold text-sm mt-2">— Maria Silva, Diarista</p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// BANNER 3: AUTÔNOMOS MEI
// ============================================
const BannerAutonomos = ({ onNavigate }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative min-h-[600px] md:min-h-[700px] rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #134e4a 50%, #0d9488 100%)',
      }}
    >
      {/* Efeitos de luz */}
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-teal-500/20 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-500/20 rounded-full blur-[100px]" />

      {/* Ícones de ferramentas flutuantes */}
      <motion.div
        animate={{ y: [0, -15, 0], x: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute top-32 right-20 text-teal-400/30"
      >
        <Wrench size={100} />
      </motion.div>

      {/* Grid */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />

      <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.3) 0%, rgba(16, 185, 129, 0.3) 100%)',
              border: '1px solid rgba(20, 184, 166, 0.5)',
            }}
          >
            <Wrench size={18} className="text-teal-400" />
            <span className="text-sm font-bold text-teal-300 uppercase tracking-wide">
              Para Autônomos
            </span>
          </motion.div>

          {/* Título */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-6xl font-black text-white leading-tight mb-6"
          >
            CHEGA DE{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
              PERDER CLIENTES
            </span>{' '}
            POR NÃO TER NOTA
          </motion.h2>

          {/* Subtítulo */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-300 mb-8"
          >
            Pedreiro, eletricista, encanador, pintor? Empresas e condomínios exigem nota fiscal.
            <span className="text-teal-400 font-semibold"> Não perca mais serviços!</span>
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-6 mb-8"
          >
            {[
              { value: '67%', label: 'mais clientes com NF' },
              { value: 'R$ 70', label: 'imposto/mês' },
              { value: '2 min', label: 'para emitir nota' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-black text-teal-400">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="flex flex-wrap gap-3 mb-8"
          >
            {['Nota Fiscal', 'DAS Automático', 'INSS', 'Conta PJ'].map((tag, i) => (
              <span 
                key={i}
                className="px-4 py-2 rounded-full text-sm font-medium text-teal-300 bg-teal-500/20 border border-teal-500/30"
              >
                ✓ {tag}
              </span>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(20, 184, 166, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate('https://app.meumei.net/register')}
            className="group px-8 py-4 rounded-xl font-bold text-white flex items-center gap-3"
            style={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
              boxShadow: '0 15px 40px rgba(20, 184, 166, 0.3)',
            }}
          >
            <Wrench size={20} />
            ABRIR MEU MEI GRÁTIS
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>

          {/* Urgência */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 flex items-center gap-2 text-yellow-400"
          >
            <Zap size={18} />
            <span className="text-sm font-medium">
              Mais de 200 autônomos se formalizaram esta semana
            </span>
          </motion.div>
        </div>
      </div>

      {/* Card flutuante de benefício */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="hidden lg:block absolute bottom-16 right-16 p-6 rounded-2xl max-w-xs"
        style={{
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
            <TrendingUp size={24} className="text-white" />
          </div>
          <div>
            <p className="text-white font-bold">Fature até</p>
            <p className="text-teal-400 text-2xl font-black">R$ 81.000/ano</p>
          </div>
        </div>
        <p className="text-gray-400 text-sm">
          Limite anual MEI. Pague menos impostos que CLT!
        </p>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// COMPONENTE PRINCIPAL: BANNERS SECTION
// ============================================
const NicheBannersSection = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const banners = [BannerDevs, BannerDomesticas, BannerAutonomos];
  const bannerNames = ['Desenvolvedores', 'Profissionais Domésticas', 'Autônomos'];

  const navigateTo = (url) => {
    window.location.href = url;
  };

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const CurrentBannerComponent = banners[currentBanner];

  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header da seção */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            <Sparkles size={16} />
            Feito para você
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4">
            Soluções para{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">
              cada profissão
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Independente da sua área, o MeuMei simplifica sua vida como MEI
          </p>
        </motion.div>

        {/* Seletor de banners */}
        <div className="flex justify-center gap-4 mb-8">
          {bannerNames.map((name, index) => (
            <button
              key={name}
              onClick={() => setCurrentBanner(index)}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                currentBanner === index
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Banner atual */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <CurrentBannerComponent key={currentBanner} onNavigate={navigateTo} />
          </AnimatePresence>

          {/* Botão Anterior - posicionado absolutamente */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={prevBanner}
            type="button"
            className="absolute top-1/2 left-4 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-800 hover:bg-white cursor-pointer"
          >
            <ChevronLeft size={24} />
          </motion.button>

          {/* Botão Próximo - posicionado absolutamente */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={nextBanner}
            type="button"
            className="absolute top-1/2 right-4 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-800 hover:bg-white cursor-pointer"
          >
            <ChevronRight size={24} />
          </motion.button>
        </div>

        {/* Indicadores */}
        <div className="flex justify-center gap-2 mt-8">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBanner(index)}
              className={`h-2 rounded-full transition-all ${
                currentBanner === index
                  ? 'w-8 bg-emerald-500'
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default NicheBannersSection;